import {
  Beneficio,
  BeneficioApi,
  BeneficioFormulario,
  Cadastro,
  CadastroApi,
  CadastroBaseApi,
  ContaBaseApi,
  ContaCadastrada,
  DadosCadastraisBase,
  DadosCadastraisBaseApi,
  Home,
  HomeApi,
  ProblemaApi,
  RegimeSimulado,
  RegimeSimuladoApi,
  Requerimento,
  RequerimentoApi,
  Resumo,
  ResumoApi,
  ResumoValores,
  ResumoValoresApi,
  SaldosRequerimento,
  SaldosRequerimentoApi,
  SimulacaoBeneficio,
  SimulacaoBeneficioApi,
  Termo,
  TermoApi,
  Tributacao,
  TributacaoApi,
  TributacaoSimulada,
  TributacaoSimuladaApi,
} from './requerimento';

function somenteDigitos(valor: string | null): string | null {
  const digitos = valor?.replace(/\D/g, '') ?? '';
  return digitos === '' ? null : digitos;
}

function textoOuNull(valor: string | null): string | null {
  const texto = valor?.trim() ?? '';
  return texto === '' ? null : texto;
}

function numeroDigitado(valor: string): number | null {
  const normalizado = valor.replace(/[^\d,]/g, '').replace(',', '.');
  return normalizado === '' ? null : Number(normalizado);
}

function montarConta(numero: string | null, digito: string | null): string | null {
  if (!numero) return null;
  return digito ? `${numero}-${digito}` : numero;
}

function separarConta(conta: string | null): Pick<CadastroApi, 'num_conta' | 'ds_digito_conta'> {
  const [numero = '', digito] = (conta ?? '').split('-');

  return {
    num_conta: somenteDigitos(numero),
    ds_digito_conta: digito === undefined ? null : textoOuNull(digito),
  };
}

function montarAgencia(numero: string | null, digito: string | null): string | null {
  if (!numero) return null;
  return digito ? `${numero}-${digito}` : numero;
}

function separarAgencia(agencia: string | null): Pick<CadastroApi, 'cod_agencia' | 'cod_digito_agencia'> {
  const [numero = '', digito] = (agencia ?? '').split('-');

  return {
    cod_agencia: somenteDigitos(numero),
    cod_digito_agencia: digito === undefined ? null : textoOuNull(digito),
  };
}

function campoDaModalidade(formulario: BeneficioFormulario): Partial<BeneficioApi> {
  switch (formulario.modalidade) {
    case 'S':
      return { pct_saldo_mensal: numeroDigitado(formulario.percentualMensal) };
    case 'P':
      return { qtd_anos_prazo: numeroDigitado(formulario.prazoAnos) };
    case 'F':
      return { vl_fixo_mensal: numeroDigitado(formulario.valorMensal) };
  }
}

function paraCamelCase(campo: string): string {
  return campo.replace(/_([a-z0-9])/g, (_, letra: string) => letra.toUpperCase());
}

export function fromApiHome(api: HomeApi): Home {
  return {
    participante: {
      nome: api.participante.nome,
      cpf: api.participante.cpf,
      codEmpresa: api.participante.cod_empresa,
      matricula: api.participante.matricula,
      codPlano: api.participante.cod_plano,
      dataNascimento: api.participante.dt_nascimento,
    },
    elegibilidade: {
      tipo: api.elegibilidade.tp_elegibilidade,
      motivos: api.elegibilidade.motivos,
    },
    institutosDisponiveis: api.institutos_disponiveis.map((instituto) => ({
      id: instituto.id,
      nome: instituto.nome,
      nomeExibicao: instituto.nome_exibicao,
      ativo: instituto.fl_status,
    })),
    requerimentoEmAndamento: api.requerimento_em_andamento
      ? fromApiRequerimento(api.requerimento_em_andamento)
      : null,
  };
}

export function fromApiRequerimento(api: RequerimentoApi): Requerimento {
  return {
    id: api.id,
    status: api.tp_status,
    etapaAtual: api.num_etapa_atual,
    protocolo: api.num_protocolo,
    cadastro: api.cadastro ? fromApiCadastro(api.cadastro) : null,
    beneficio: api.beneficio ? fromApiBeneficio(api.beneficio) : null,
    tributacao: api.tributacao ? fromApiTributacao(api.tributacao) : null,
  };
}

export function fromApiCadastro(api: CadastroApi): Cadastro {
  return {
    emailPrincipal: api.ds_email_princ,
    emailSecundario: api.ds_email_sec,
    dddCelular: api.cod_ddd_celular,
    celular: api.num_celular,
    cep: api.cod_cep,
    uf: api.cod_uf,
    cidade: api.nom_cidade,
    bairro: api.nom_bairro,
    endereco: api.ds_endereco,
    numeroEndereco: api.num_endereco,
    complemento: api.ds_complemento,
    resideExterior: api.fl_reside_exterior,
    paisExterior: api.nom_pais_exterior,
    zipExterior: api.cod_zip_exterior,
    nifExterior: api.num_nif_exterior,
    codBanco: api.cod_banco,
    nomeBanco: api.nom_banco ?? null,
    agencia: montarAgencia(api.cod_agencia, api.cod_digito_agencia ?? null),
    conta: montarConta(api.num_conta, api.ds_digito_conta),
    molestiaGrave: api.fl_molestia_grave ?? null,
    dtOrigemCadastro: api.dt_origem_cadastro ?? null,
  };
}

/** Contas da base da mais recente (maior id_dado_bancario) para a mais antiga. */
function contasRecentesPrimeiro(contas: ContaBaseApi[]): ContaBaseApi[] {
  return [...contas].sort((a, b) => b.id_dado_bancario - a.id_dado_bancario);
}

/** Cadastro da base unificada como o formulário da etapa 1 (primeira visita). Conta: a de maior id_dado_bancario. */
export function fromApiCadastroBase(api: CadastroBaseApi): Cadastro {
  const conta = contasRecentesPrimeiro(api.contas)[0] ?? null;

  return {
    emailPrincipal: api.email_principal,
    emailSecundario: api.email_secundario,
    dddCelular: api.ddd_celular?.toString() ?? null,
    celular: api.celular?.toString() ?? null,
    cep: api.cep,
    uf: api.uf,
    cidade: api.cidade,
    bairro: api.bairro,
    endereco: api.logradouro,
    numeroEndereco: api.numero,
    complemento: api.complemento,
    resideExterior: api.residente_exterior,
    paisExterior: null,
    zipExterior: null,
    nifExterior: null,
    codBanco: conta?.codigo_banco ?? null,
    nomeBanco: conta?.nome_banco ?? null,
    agencia: conta ? montarAgencia(conta.agencia, conta.digito_agencia) : null,
    conta: conta ? montarConta(conta.conta, conta.digito_conta) : null,
    molestiaGrave: null,
  };
}

export function fromApiContaBase(api: ContaBaseApi): ContaCadastrada {
  return {
    codBanco: api.codigo_banco ?? 0,
    nomeBanco: api.nome_banco ?? '',
    agencia: montarAgencia(api.agencia, api.digito_agencia) ?? '',
    conta: montarConta(api.conta, api.digito_conta) ?? '',
  };
}

/** GET dados-cadastrais-base: cadastro (com a conta mais recente), todas as contas e as listas de bancos e países. */
export function fromApiDadosCadastraisBase(api: DadosCadastraisBaseApi): DadosCadastraisBase {
  return {
    cadastro: fromApiCadastroBase(api.cadastro),
    contas: contasRecentesPrimeiro(api.cadastro.contas).map(fromApiContaBase),
    bancos: api.bancos ?? [],
    paises: api.paises ?? [],
  };
}

export function fromApiBeneficio(api: BeneficioApi): Beneficio {
  return {
    modalidade: api.tp_modalidade_receb,
    percentualAntecipacaoSaldo: api.pct_antecip_saldo,
    percentualSaldoMensal: api.pct_saldo_mensal,
    qtdAnosPrazo: api.qtd_anos_prazo,
    valorFixoMensal: api.vl_fixo_mensal,
    adiantaBeneficio: api.fl_adiant_benef,
  };
}

export function fromApiTributacao(api: TributacaoApi): Tributacao {
  return {
    regime: api.tp_regime_ir,
    qtdDependentes: api.qtd_depend_ir,
  };
}

export function fromApiTermo(api: TermoApi): Termo {
  return {
    hashTermo: api.hash_termo,
    aceito: api.aceite !== null,
    dataAssinatura: api.aceite?.dt_assinatura ?? null,
  };
}

function fromApiRegimeSimulado(regime: RegimeSimuladoApi): RegimeSimulado {
  return {
    regime: regime.tp_regime_ir,
    aliquotaEfetiva: regime.aliquota_efetiva,
    valorIr: regime.valor_ir,
    valorLiquido: regime.valor_liquido,
    observacao: regime.observacao,
  };
}

export function fromApiTributacaoSimulada(api: TributacaoSimuladaApi): TributacaoSimulada {
  return {
    valorBruto: api.valor_bruto,
    regimes: api.regimes.map(fromApiRegimeSimulado),
    retorno: JSON.stringify(api),
  };
}

export function fromApiSimulacaoBeneficio(api: SimulacaoBeneficioApi): SimulacaoBeneficio {
  return {
    valorBruto: api.valor_bruto,
    regimes: api.regimes.map(fromApiRegimeSimulado),
  };
}

export function fromApiSaldosRequerimento({ saldos, limites }: SaldosRequerimentoApi): SaldosRequerimento {
  const { antecipacao, opcoes_renda: renda } = limites;

  return {
    saldoParticipante: saldos.total_participante,
    saldoPatrocinadora: saldos.total_patrocinador,
    saldoTotal: saldos.total,
    limites: {
      permiteAntecipacao: antecipacao.fl_permite_antecip_sld,
      percentualMinimoAntecipacao: antecipacao.pc_min_antecip_sld,
      percentualMaximoAntecipacao: antecipacao.pc_max_antecip_sld,
      elegivelAdiantamento: antecipacao.fl_elegivel_adiant,
      permiteValorFixoMensal: renda.fl_vlr_fixo_mensal,
      permitePercentualSaldo: renda.fl_pct_saldo,
      percentualMinimoMensal: renda.pct_saldo_min,
      percentualMaximoMensal: renda.pct_saldo_max,
      permitePrazoCerto: renda.fl_prazo_certo,
      prazoMinimoAnos: renda.qtd_prazo_min,
      prazoMaximoAnos: renda.qtd_prazo_max,
    },
  };
}

export function fromApiResumo(api: ResumoApi): Resumo {
  return {
    saldoParticipante: api.vl_cta_contrib_partic,
    saldoPatrocinadora: api.vl_cta_contrib_patroc,
    percentualAntecipacao: api.pct_antecip_saldo,
    valorAntecipacao: api.vl_antecip_saldo,
    valorBrutoMensal: api.vl_bruto_mensal,
    percentualImpostoRenda: api.pct_aliquota_ir,
    valorImpostoRenda: api.vl_ir_mensal,
    valorLiquidoMensal: api.vl_liquido_mensal,
    tempoEstimadoAnos: api.qtd_anos_estimado,
    qtdParcelas: api.qtd_parcelas,
    valorPrimeiraParcela: api.vl_prim_parcela,
    dataPrimeiraParcela: api.dt_prim_parcela,
    valorBrutoResgate: api.vl_bruto_resgate,
    valorImpostoRendaResgate: api.vl_ir_resgate,
    valorLiquidoResgate: api.vl_liquido_resgate,
    retornoCalculo: api.ds_retorno_calculo,
    dataCalculo: api.dt_calculo,
    congelado: api.dt_congelamento !== null,
  };
}

export function toResumoPayload(valores: ResumoValores): ResumoValoresApi {
  return {
    vl_cta_contrib_partic: valores.saldoParticipante,
    vl_cta_contrib_patroc: valores.saldoPatrocinadora,
    pct_antecip_saldo: valores.percentualAntecipacao,
    vl_antecip_saldo: valores.valorAntecipacao,
    vl_bruto_mensal: valores.valorBrutoMensal,
    pct_aliquota_ir: valores.percentualImpostoRenda,
    vl_ir_mensal: valores.valorImpostoRenda,
    vl_liquido_mensal: valores.valorLiquidoMensal,
    qtd_anos_estimado: valores.tempoEstimadoAnos,
    qtd_parcelas: valores.qtdParcelas,
    vl_prim_parcela: valores.valorPrimeiraParcela,
    dt_prim_parcela: valores.dataPrimeiraParcela,
    vl_bruto_resgate: valores.valorBrutoResgate,
    vl_ir_resgate: valores.valorImpostoRendaResgate,
    vl_liquido_resgate: valores.valorLiquidoResgate,
    ds_retorno_calculo: valores.retornoCalculo,
  };
}

export function toCadastroPayload(cadastro: Cadastro): Partial<CadastroApi> {
  const exterior = cadastro.resideExterior;

  return {
    ds_email_princ: cadastro.emailPrincipal.trim(),
    ds_email_sec: textoOuNull(cadastro.emailSecundario),
    cod_ddd_celular: somenteDigitos(cadastro.dddCelular),
    num_celular: somenteDigitos(cadastro.celular),
    fl_reside_exterior: exterior,
    cod_cep: somenteDigitos(cadastro.cep),
    cod_uf: textoOuNull(cadastro.uf),
    nom_cidade: textoOuNull(cadastro.cidade),
    nom_bairro: textoOuNull(cadastro.bairro),
    ds_endereco: textoOuNull(cadastro.endereco),
    num_endereco: textoOuNull(cadastro.numeroEndereco),
    ds_complemento: textoOuNull(cadastro.complemento),
    nom_pais_exterior: exterior ? textoOuNull(cadastro.paisExterior) : null,
    cod_zip_exterior: exterior ? textoOuNull(cadastro.zipExterior) : null,
    num_nif_exterior: exterior ? textoOuNull(cadastro.nifExterior) : null,
    cod_banco: cadastro.codBanco,
    ...separarAgencia(cadastro.agencia),
    ...separarConta(cadastro.conta),
    fl_molestia_grave: cadastro.molestiaGrave,
  };
}

export function toBeneficioPayload(formulario: BeneficioFormulario): Partial<BeneficioApi> {
  return {
    // Opcional na tela: sem valor informado, vai 0 (sem antecipação), o que a API exige para dar a etapa 2 como completa.
    pct_antecip_saldo: numeroDigitado(formulario.percentualAntecipacao) ?? 0,
    tp_modalidade_receb: formulario.modalidade,
    fl_adiant_benef: formulario.adiantaBeneficio,
    ...campoDaModalidade(formulario),
  };
}

/**
 * Querystring do GET /simulacao: a modalidade com só o campo dela, a antecipação só quando informada e o
 * adiantamento do checkbox.
 */
export function toSimulacaoParams(formulario: BeneficioFormulario): Record<string, string | number | boolean> {
  const campos: Partial<BeneficioApi> = {
    tp_modalidade_receb: formulario.modalidade,
    ...campoDaModalidade(formulario),
    pct_antecip_saldo: numeroDigitado(formulario.percentualAntecipacao),
    fl_adiant_benef: formulario.adiantaBeneficio,
  };
  return Object.fromEntries(
    Object.entries(campos).filter(([, valor]) => valor !== null && valor !== undefined),
  ) as Record<string, string | number | boolean>;
}

export function toBeneficioApi(beneficio: Beneficio): BeneficioApi {
  return {
    pct_antecip_saldo: beneficio.percentualAntecipacaoSaldo,
    tp_modalidade_receb: beneficio.modalidade,
    pct_saldo_mensal: beneficio.percentualSaldoMensal,
    qtd_anos_prazo: beneficio.qtdAnosPrazo,
    vl_fixo_mensal: beneficio.valorFixoMensal,
    fl_adiant_benef: beneficio.adiantaBeneficio,
  };
}

export function toTributacaoPayload(tributacao: Tributacao): Partial<TributacaoApi> {
  return {
    tp_regime_ir: tributacao.regime,
    qtd_depend_ir: tributacao.qtdDependentes,
  };
}

export function mapearErrosApi(problema: ProblemaApi): Record<string, string[]> {
  return Object.fromEntries(
    Object.entries(problema.errors ?? {}).map(([chave, mensagens]) => [
      paraCamelCase(chave.split('.').pop() ?? chave),
      mensagens,
    ]),
  );
}
